<script lang="ts">
import { defineComponent, ref } from 'vue'
import ButtonLpCta from '@/components/button/ButtonLpCta.vue'
import textHeroPC1 from '@/images/about/text-hero_pc_01.svg'
import textHeroSP from '@/images/about/text-hero_sp_01.svg'
import achievementImage01 from '@/images/about/text-achievement_01.svg'
import achievementImage02 from '@/images/about/text-achievement_02.svg'
import achievementImage03 from '@/images/about/text-achievement_03.svg'
import { newUserRegistrationPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'AboutHero',

  components: {
    ButtonLpCta,
  },

  props: {
    ctaButtonLabel: {
      type: String,
      required: true,
    },

    trackingCodePrefix: {
      type: String,
      default: '',
    },
  },

  setup() {
    // キャッチコピー画像のスライドイン演出は、画像の読み込み完了を起点にする
    const isTextHeroLoaded = ref(false)

    const ACHIEVEMENT_ITEMS = [
      {
        image: achievementImage01,
        label: '累計会員数70万人以上',
      },
      {
        image: achievementImage02,
        label: '経営者や役員20万人以上',
      },

      {
        image: achievementImage03,
        label: '事例記事660社以上',
      },
    ] as const

    const achievementImages = import.meta.glob('@/images/about/text-achievement_*.svg', { eager: true })
    const achievementImageUrls = Object.values(achievementImages).map((m: any) => m.default)

    return {
      // constants
      ACHIEVEMENT_ITEMS,

      // routes
      newUserRegistrationPath,

      // data
      isTextHeroLoaded,
      achievementImageUrls,
      textHeroPC1,
      textHeroSP,
    }
  },
})
</script>

<template>
  <section class="f-section__hero">
    <div class="f-section__hero--body">
      <div class="l-unit__hero">
        <div class="l-unit__hero--header">
          <div class="o-heading__page">
            <h1 class="o-heading__page--item">
              <picture class="o-figure__catchCopy">
                <!-- media の値は _variables.sass の $global-width-tablet（984 + 20*2）と一致させる -->
                <source
                  class="o-figure__catchCopy--item is-source"
                  media="(max-width: 1024px)"
                  :srcset="textHeroSP">
                <img
                  class="o-figure__catchCopy--item is-entity"
                  :src="textHeroPC1"
                  fetchpriority="high"
                  alt="経営判断に迷う時間を、ゼロにする。"
                  :data-loaded="isTextHeroLoaded"
                  data-is-liquid="true"
                  @load="isTextHeroLoaded = true">
              <!-- /.o-figure__catchCopy --></picture>
            </h1>
          <!-- /.o-heading__page --></div>
        <!-- /.l-unit__hero header --></div>

        <div class="l-unit__hero--image">
          <div class="o-list__achievement">
            <ul class="o-list__achievement--items">
              <li
                v-for="(item, index) in ACHIEVEMENT_ITEMS"
                :key="item.label"
                class="o-list__achievement--item"
                :data-index="index">
                <p class="o-label__achievement--item">
                  <img
                    class="img"
                    :src="item.image"
                    :alt="item.label">
                </p>
              </li>
            </ul>
          <!-- /.o-list__achievement --></div>
        <!-- /.l-unit__hero image --></div>

        <div class="l-unit__hero--description">
          <div class="o-label__leadCopy">
            <p class="o-label__leadCopy--item">
              20万人の経営者・役職者が活用。<br>
              課題解決に直結する「記事・資料・ノウハウ・他社事例」を、
              <span class="span u-text__block">最短で入手できます。</span>
            </p>
          <!-- /.o-label__leadCopy --></div>
        <!-- /.l-unit__hero description --></div>

        <div class="l-unit__hero--action">
          <ButtonLpCta
            :href="newUserRegistrationPath({ trcd: `${trackingCodePrefix}_hero` })"
            font-size="xLarge"
            size="normal"
            animate>
            <span
              class="span"
              v-html="ctaButtonLabel" />
          </ButtonLpCta>
        <!-- /.l-unit__hero action --></div>
      <!-- /.l-unit__hero --></div>
    <!-- /.f-section__hero body --></div>
  <!-- /.f-section__hero --></section>
</template>

<style lang="sass" scoped>
$hero-contens-width-pc: 1240

// foundation
.f-section__hero
  min-height: 657px
  position: relative
  padding: 2em #{$global-prop-sp}px
  background:
    image: url('@/images/about/bg-hero_01.jpg')
    size: cover
    position: center
    repeat: no-repeat

  & &--body
    width: 100%
    max-width: #{$hero-contens-width-pc}px
    position: relative
    margin: 0 auto

  +breakpoint($max-media: tablet)
    background:
      color: rgba($bg-white, 1)
      image: linear-gradient(to right, #e2f2ff 0%, $bg-white 70%)

// layout
.l-unit__hero
  $root: &
  display: flex
  flex-direction: column

  & &--header
    max-width: 900px
    margin-bottom: 1em

  & &--image
    order: 1
    margin-top: 2.5em

  & &--action
    margin-top: 1.5em

  & &--image,
  & &--description,
  & &--action
    max-width: 560px

  +breakpoint($max-media: tablet)
    #{$root}--image,
    #{$root}--description,
    #{$root}--action
      max-width: none

    #{$root}--action
      order: 2
      margin-top: 2em

// object
.o-heading__page
  & &--item
    line-height: 1

.o-figure__catchCopy
  // picture 要素は既定が inline のため、div だった頃の block 表示を明示する
  display: block

  & &--item
    &.is-entity
      display: block
      line-height: 1
      transform: translateX(-32px)
      opacity: 0
      +transition($duration: 2s)

      &[data-loaded="true"]
        transform: translateX(0)
        opacity: 1

.o-label__leadCopy
  &:first-child
    margin-top: 0

  & &--item
    line-height: 1.5
    color: #2c3e50
    font-size: $font-x-large

.o-list__achievement
  $root: &

  & &--items
    display: grid
    grid-template-columns: repeat(3, minmax(0, 150px))
    align-items: center
    column-gap: 1em

  +breakpoint($max-media: tablet)
    #{$root}--items
      grid-template-columns: repeat(3, 1fr)
      column-gap: getRelativeValue(16, 375)
</style>
